<template>
  <b-card body-class="governing-bodies__card">
    <h6 class="dashboard-pages__title">Document Upload</h6>
    <p class="document-explanation">
      Upload your driving licence, passport, or other identity documents.
    </p>

    <SearchableDropdown
      v-model="selectedDocumentType"
      :options="documentTypeOptions"
      placeholder="Search document types..."
      :group-by="'category'"
    />

    <div v-if="selectedDocumentType" class="upload-section">
      <b-form-group
        label="Document Name"
        label-class="font-weight-600"
        class="mb-3"
      >
        <b-form-input
          v-model="customFilename"
          placeholder="Enter name for your document"
          maxlength="100"
          :class="documentNameValidationClass"
        />
        <small class="text-muted"
          >This name will help you identify this document</small
        >
      </b-form-group>

      <b-form-group
        :label="expiryDateLabel"
        label-class="font-weight-600 label-title"
        class="mb-3"
        :description="'Enter the expiry date of your document'"
      >
        <div
          class="date-validation-wrapper"
          :class="{
            'validation-error':
              expiryDateValidationClass === 'validation-error',
            'validation-success':
              expiryDateValidationClass === 'validation-success',
          }"
        >
          <Datepicker
            v-model="dateValue"
            :bootstrap-styling="true"
            :clear-button="true"
            :format="'dd/MM/yyyy'"
            @selected="onDateSelected"
            @cleared="onDateCleared"
          />
        </div>
      </b-form-group>

      <Upload :item="uploadItem" :disabled="!isFormValid" />
    </div>
  </b-card>
</template>

<script>
import Upload from "../form/Upload.vue";
import Datepicker from "vuejs-datepicker";
import SearchableDropdown from "../common/SearchableDropdown.vue";
import {
  DOCUMENT_TYPES,
  getUploadDocumentTypes,
  getDocumentTypeText,
} from "@/constants/documentTypes";

export default {
  name: "ProfileDocumentUpload",
  components: {
    Upload,
    Datepicker,
    SearchableDropdown,
  },
  props: {
    personalProfile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedDocumentType: null,
      uploadItem: {
        title: "Document Upload",
        helpText: "Upload your selected ID document.",
        value: [],
        acceptTypes: [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/gif",
          "image/bmp",
          "image/webp",
          "application/pdf",
        ],
      },
      isUploading: false,
      customFilename: "",
      expiryDate: "",
      dateValue: null,
    };
  },
  watch: {
    "uploadItem.value": {
      handler(newValue, oldValue) {
        if (newValue && newValue.length > 0) {
          if (!oldValue || newValue.length > oldValue.length) {
            const lastItem = newValue[newValue.length - 1];

            if (lastItem && !lastItem.type && this.selectedDocumentType) {
              this.isUploading = true;
              lastItem.type = this.selectedDocumentType;

              this.updateUserProfile(
                lastItem.id,
                this.selectedDocumentType,
                lastItem.name
              );

              this.$nextTick(() => {
                this.uploadItem.value = [];
                this.selectedDocumentType = null;
                this.customFilename = "";
                this.expiryDate = "";
              });

              this.isUploading = false;
            }
          }
        }
      },
      deep: true,
    },
    selectedDocumentType(newType) {
      if (newType) {
        this.updateUploadItemTitle();
        this.customFilename = "";
        this.expiryDate = "";
      }
    },
  },
  computed: {
    documentTypeOptions() {
      return getUploadDocumentTypes();
    },
    isExpiryDateRequired() {
      return (
        this.selectedDocumentType === "DRIVING_LICENCE" ||
        this.selectedDocumentType === "PASSPORT"
      );
    },

    expiryDateLabel() {
      return this.isExpiryDateRequired
        ? "Expiry Date *"
        : "Expiry Date (Optional)";
    },

    isFormValid() {
      const hasDocumentName = this.customFilename.trim().length > 0;
      const hasRequiredExpiry =
        !this.isExpiryDateRequired ||
        (this.isExpiryDateRequired && this.expiryDate);

      return hasDocumentName && hasRequiredExpiry;
    },

    documentNameValidationClass() {
      if (this.customFilename.trim().length === 0) {
        return "validation-error";
      } else {
        return "validation-success";
      }
    },

    expiryDateValidationClass() {
      if (this.isExpiryDateRequired && !this.expiryDate) {
        return "validation-error";
      } else if (this.expiryDate) {
        return "validation-success";
      } else {
        return "";
      }
    },
  },
  methods: {
    updateUploadItemTitle() {
      const displayName = getDocumentTypeText(this.selectedDocumentType);
      this.uploadItem.title = `Upload ${displayName} Photo`;
      this.uploadItem.helpText = `Upload your ${displayName.toLowerCase()}.`;
    },

    getStandardizedFilename(documentType, originalFilename) {
      const extension = originalFilename.split(".").pop();
      switch (documentType) {
        case "DRIVING_LICENCE":
          return `Driving_Licence_Photo.${extension}`;
        case "PASSPORT":
          return `Passport_Photo.${extension}`;
        default:
          return originalFilename;
      }
    },

    getCustomFilename(originalFilename) {
      if (this.selectedDocumentType) {
        return this.getStandardizedFilename(
          this.selectedDocumentType,
          originalFilename
        );
      }
      return originalFilename;
    },

    getCurrentDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },

    validateInputs() {
      if (!this.customFilename.trim()) {
        this.$error("Validation Error", "Please enter a document name.");
        return false;
      }

      if (this.isExpiryDateRequired && !this.expiryDate) {
        this.$error(
          "Validation Error",
          "Please enter the expiry date for your document."
        );
        return false;
      }

      return true;
    },

    onDateSelected(date) {
      this.expiryDate = date ? this.formatDateForBackend(date) : "";
    },

    onDateCleared() {
      this.expiryDate = "";
    },

    formatDateForBackend(date) {
      // Format date as YYYY-MM-DD for backend
      return date.toISOString().split("T")[0];
    },

    updateUserProfile(storageId, documentType, originalFilename) {
      if (!this.validateInputs()) {
        return;
      }

      // documentType is already the enum value from the SearchableDropdown
      const identityDocumentData = {
        uploadStorageId: storageId,
        type: documentType, // Already the enum value (e.g., 'DRIVING_LICENCE', 'PASSPORT', etc.)
        name: this.customFilename.trim(),
      };
      if (this.expiryDate) {
        identityDocumentData.expiryDate = this.expiryDate;
      }

      // Get the display text for success message
      const displayName = getDocumentTypeText(documentType);

      this.$api.v2
        .post("identity-documents", identityDocumentData)
        .then((response) => {
          this.$success(
            "Document Uploaded",
            `${displayName} has been uploaded and linked to your profile successfully.`
          );

          this.$emit("document-uploaded", response.body);
        })
        .catch((error) => {
          console.error(
            "[ProfileDocumentUpload] Failed to create identity document:",
            {
              request: identityDocumentData,
              error: error,
              errorMessage: error.message,
              errorStatus: error.status,
              errorBody: error.body,
              timestamp: new Date().toISOString(),
            }
          );

          this.$httpError("Failed to link document to profile", error);
        });
    },
  },
  mounted() {
    this.uploadItem.getCustomFilename = this.getCustomFilename;
  },
};
</script>

<style scoped>
.font-weight-600 {
  font-weight: 600;
}

.upload-section {
  margin-top: 1rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.document-explanation {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.dashboard-pages__title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

/* Validation styling */
.validation-error {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.validation-error:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.validation-success {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}

.validation-success:focus {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}

/* Datepicker validation styling */
/* Error state */
.date-validation-wrapper.validation-error >>> .vdp-datepicker input {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.date-validation-wrapper.validation-error >>> .vdp-datepicker input:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.date-validation-wrapper.validation-error >>> .vdp-datepicker__clear-button {
  border-color: #dc3545 !important;
  border-left-color: #dc3545 !important;
  border-right-color: #dc3545 !important;
  border-top-color: #dc3545 !important;
  border-bottom-color: #dc3545 !important;
  background-color: #dc3545 !important;
  color: white !important;
}

.date-validation-wrapper.validation-error >>> .input-group-append {
  border-color: #dc3545 !important;
}

.date-validation-wrapper.validation-error >>> .input-group-append .btn {
  border-color: #dc3545 !important;
  border-left-color: #dc3545 !important;
  border-right-color: #dc3545 !important;
  border-top-color: #dc3545 !important;
  border-bottom-color: #dc3545 !important;
}

/* Success state */
.date-validation-wrapper.validation-success >>> .vdp-datepicker input {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}

.date-validation-wrapper.validation-success >>> .vdp-datepicker input:focus {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}

.date-validation-wrapper.validation-success >>> .vdp-datepicker__clear-button {
  border-color: #28a745 !important;
  border-left-color: #28a745 !important;
  border-right-color: #28a745 !important;
  border-top-color: #28a745 !important;
  border-bottom-color: #28a745 !important;
  background-color: #28a745 !important;
  color: white !important;
}

.date-validation-wrapper.validation-success >>> .input-group-append {
  border-color: #28a745 !important;
}

.date-validation-wrapper.validation-success >>> .input-group-append .btn {
  border-color: #28a745 !important;
  border-left-color: #28a745 !important;
  border-right-color: #28a745 !important;
  border-top-color: #28a745 !important;
  border-bottom-color: #28a745 !important;
}

/* Focus state for the entire input group */
.date-validation-wrapper.validation-error >>> .vdp-datepicker:focus-within {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.date-validation-wrapper.validation-success >>> .vdp-datepicker:focus-within {
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}
</style>
